import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Image as ImageIcon } from 'lucide-react';
import ImageModal from './ImageModal';
import { GalleryImage, getGalleryThumbnailUrl } from '../../services/galleryService';
import countryColors from '../../config/country_colors.json';

interface PhotosSectionProps {
  images: GalleryImage[];
}

interface CountryGroup {
  country: string;
  description?: string;
  images: GalleryImage[];
}

interface YearGroup {
  year: string;
  countries: CountryGroup[];
}

const getCountryColors = (country: string): { primary: string; secondary: string | null } => {
  const colors = (countryColors as Record<string, { primary: string; secondary: string | null }>)[country];
  return colors || { primary: '#f59e0b', secondary: null };
};

const PhotosSection: React.FC<PhotosSectionProps> = ({ images }) => {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<GalleryImage[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const yearGroups = useMemo((): YearGroup[] => {
    const photos = images.filter((img) => img.filename && img.filename.trim() !== '');

    const byYear: Record<string, Record<string, GalleryImage[]>> = {};
    photos.forEach((img) => {
      if (!byYear[img.year]) byYear[img.year] = {};
      if (!byYear[img.year][img.country]) byYear[img.year][img.country] = [];
      byYear[img.year][img.country].push(img);
    });

    return Object.entries(byYear)
      .sort(([a], [b]) => Number(b) - Number(a))
      .map(([year, countriesMap]) => ({
        year,
        countries: Object.entries(countriesMap)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([country, countryImages]) => ({
            country,
            description: countryImages.find((i) => i.description?.trim())?.description,
            images: countryImages,
          })),
      }));
  }, [images]);

  const handlePhotoClick = (country: string, groupImages: GalleryImage[]) => {
    setSelectedCountry(country);
    setSelectedImages(groupImages);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      setSelectedCountry(null);
      setSelectedImages([]);
    }, 300);
  };

  if (yearGroups.length === 0) {
    return (
      <div className="text-center py-16">
        <ImageIcon className="w-16 h-16 mx-auto mb-4 text-slate-400 dark:text-slate-500" />
        <p className="text-lg text-slate-600 dark:text-slate-400">
          No photos available yet. Check back soon!
        </p>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-16"
      >
        {yearGroups.map((yearGroup) => (
          <section key={yearGroup.year}>
            <div className="flex items-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white font-serif mr-4">
                {yearGroup.year}
              </h2>
              <div className="flex-1 h-px bg-slate-300 dark:bg-slate-600" />
            </div>

            <div className="space-y-12">
              {yearGroup.countries.map((group) => {
                const colors = getCountryColors(group.country);
                return (
                  <div key={`${yearGroup.year}-${group.country}`}>
                    <h3
                      className="text-xl md:text-2xl font-bold font-serif mb-2"
                      style={{ color: colors.primary }}
                    >
                      {group.country}
                    </h3>
                    {group.description && (
                      <p className="text-slate-600 dark:text-slate-400 mb-5 max-w-3xl">
                        {group.description}
                      </p>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {group.images.map((image, index) => (
                        <motion.button
                          key={image.id}
                          type="button"
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.04, duration: 0.4 }}
                          onClick={() => handlePhotoClick(group.country, group.images)}
                          className="group relative aspect-[4/3] rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                        >
                          <img
                            src={getGalleryThumbnailUrl(image)}
                            alt={`${group.country} ${image.year}`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </motion.button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </motion.div>

      {selectedCountry && selectedImages.length > 0 && (
        <ImageModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          country={selectedCountry}
          images={selectedImages}
        />
      )}
    </>
  );
};

export default PhotosSection;
